/* eslint-disable jira/choreographer/message-id-by-folder-structure */
import React, { useEffect, type ReactNode } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { CUSTOMER_SERVICE_PROJECT } from '@jira/platform__project-types/src/project-types.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { ScreenRedirect } from '@jira/platform__routing-screen-redirect/src/ui/ScreenRedirect.tsx';
import { createRouterSelector } from '@atlassian/react-resource-router';
import { useProjectContext } from '@jira/platform__services-providers-project-context/src/index.tsx';
import { useFlagService } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { HelpHelpCenterAri } from '@atlassian/ari/help/help-center';
import { isHelpCenterSettingsPath } from './isHelpCenterSettingsPath.tsx';
import { getSettingsPath } from './getSettingsPath.tsx';
import type { HelpCenterSettingsRedirectOldQuery } from './__generated__/HelpCenterSettingsRedirectOldQuery.graphql.ts';

const errorTitle = defineMessage({
	id: 'servicedesk-customer-service-common.help-center-settings-redirect.error-title',
	defaultMessage: 'This page has been moved',
	description:
		'Title of error flag shown when a user visits a help center settings page from a project settings url on a JCS project',
});

const errorDescription = defineMessage({
	id: 'servicedesk-customer-service-common.help-center-settings-redirect.error-description',
	defaultMessage: 'You can find the page you’re looking for in a service hub.',
	description:
		'Description on error flag shown when a user visits a help center settings page from a project settings url on a JCS project',
});

type Page =
	| 'kb'
	| 'customer-notifications'
	| 'email'
	| 'ticket-types-config'
	| 'ticket-type-fields';

type Props = {
	page: Page;
	children: ReactNode;
};

export const getPageForAnalytics = (
	page: Page,
	pathname: string,
):
	| Page
	| 'customer-notifications-template'
	| 'customer-notifications-invite-customer'
	| 'customer-notifications-rule' => {
	if (page === 'customer-notifications') {
		const customerNotificationsPage = pathname.split('/settings/customer-notifications')[1];

		if (customerNotificationsPage === '/template') {
			return 'customer-notifications-template';
		}
		if (customerNotificationsPage === '/account-notification/invite-customer') {
			return 'customer-notifications-invite-customer';
		}
		if (/rule\/\d+/.test(customerNotificationsPage)) {
			return 'customer-notifications-rule';
		}
	}

	return page;
};

export const getRedirectTo = (page: Page, pathname: string, helpCenterId?: string): string => {
	switch (page) {
		case 'kb':
			return getSettingsPath(pathname.replace('/confluence-kb', '/kb'), true, helpCenterId);
		default:
			return getSettingsPath(pathname, true, helpCenterId);
	}
};

const useLocation = createRouterSelector((state) => state.location);

// This component is used for redirecting users from project settings pages to
// their help center settings equivalents
export const HelpCenterSettingsRedirect = ({
	page,
	children,
}: Props):
	| string
	| number
	| boolean
	| Iterable<React.ReactNode>
	| React.JSX.Element
	| null
	| undefined => {
	const location = useLocation();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data: projectContext } = useProjectContext();
	const isJCS = projectContext?.projectType === CUSTOMER_SERVICE_PROJECT;

	useEffect(() => {
		if (isJCS && !isHelpCenterSettingsPath()) {
			fireTrackAnalytics(
				createAnalyticsEvent({}),
				'projectSettingsToHelpCenterSettings redirected',
				{
					page: getPageForAnalytics(page, location.pathname),
				},
			);
		}
	}, [createAnalyticsEvent, isJCS, location.pathname, page]);

	if (isJCS && !isHelpCenterSettingsPath()) {
		return (
			<HelpCenterSettingsRedirectWithHelpCenterId
				page={page}
				pathname={location.pathname}
				projectId={projectContext.projectId}
			/>
		);
	}

	return children;
};

export const HelpCenterSettingsRedirectWithHelpCenterId = ({
	page,
	pathname,
	projectId,
}: {
	page: Page;
	pathname: string;
	projectId: number | undefined;
}): React.JSX.Element => {
	const { showFlag } = useFlagService();
	const { formatMessage } = useIntl();
	const activationId = useActivationId();
	const cloudId = useCloudId();
	const jiraProjectAri = `ari:cloud:jira:${cloudId}:project/${projectId}`;
	const jiraWorkspaceAri = `ari:cloud:jira:${cloudId}:workspace/${activationId}`;

	const {
		helpCenter: { helpCenterByHoistedProjectIdRouted: helpCenter },
	} = useLazyLoadQuery<HelpCenterSettingsRedirectOldQuery>(
		graphql`
			query HelpCenterSettingsRedirectOldQuery(
				$cloudId: ID!
				$jiraProjectAri: ID!
				$jiraWorkspaceAri: ID!
			) {
				helpCenter(cloudId: $cloudId) @required(action: THROW) {
					helpCenterByHoistedProjectIdRouted(
						projectAri: $jiraProjectAri
						workspaceAri: $jiraWorkspaceAri
					) @optIn(to: ["HelpCenterSlugTest", "HelpCenterAggBeta"]) @required(action: THROW) {
						__typename
						... on HelpCenter {
							id
						}
					}
				}
			}
		`,
		{ cloudId, jiraProjectAri, jiraWorkspaceAri },
	);

	if (helpCenter.__typename !== 'HelpCenter') {
		showFlag({
			messageId:
				'servicedesk-customer-service-common.ui.help-center-settings-redirect.show-flag.error.1',
			messageType: 'transactional',
			type: 'error',
			title: formatMessage(errorTitle),
			description: formatMessage(errorDescription),
			isAutoDismiss: true,
		});
		return <ScreenRedirect to="/jira/helpcenter" />;
	}

	return (
		<ScreenRedirect
			to={getRedirectTo(page, pathname, HelpHelpCenterAri.parse(helpCenter.id).helpCenterId)}
			persistSearchParams
		/>
	);
};
